import { Component } from '@theme/component';

/**
 * Tabs over a bundle's products, one panel per product.
 * WAI-ARIA tabs (horizontal): a click or Left/Right selects, Home/End jump to the ends.
 *
 * @typedef {object} BundleTabsRefs
 * @property {HTMLButtonElement[]} tabs
 * @property {HTMLElement[]} panels
 *
 * @extends {Component<BundleTabsRefs>}
 */
class BundleTabsComponent extends Component {
  requiredRefs = ['tabs', 'panels'];
  #index = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#show(this.#index);
  }

  updatedCallback() {
    super.updatedCallback();
    // A re-render brings every tab back unselected; keep the shopper's tab
    this.#show(Math.min(this.#index, this.refs.tabs.length - 1));
  }

  /** @param {Event} event */
  select(event) {
    if (!(event.target instanceof HTMLButtonElement)) return;
    const index = this.refs.tabs.indexOf(event.target);
    if (index >= 0) this.#show(index);
  }

  /** @param {KeyboardEvent} event */
  onKeydown(event) {
    if (!(event.target instanceof HTMLButtonElement)) return;
    const { tabs } = this.refs;
    const index = tabs.indexOf(event.target);
    if (index < 0) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    const forward = rtl ? 'ArrowLeft' : 'ArrowRight';
    const back = rtl ? 'ArrowRight' : 'ArrowLeft';
    let next = -1;
    if (event.key === forward) next = (index + 1) % tabs.length;
    else if (event.key === back) next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    if (next < 0) return;
    event.preventDefault();
    this.#show(next, true);
  }

  /**
   * @param {number} index
   * @param {boolean} [focus]
   */
  #show(index, focus = false) {
    const { tabs, panels } = this.refs;
    this.#index = index;
    for (const [i, tab] of tabs.entries()) {
      tab.setAttribute('aria-selected', String(i === index));
      tab.tabIndex = i === index ? 0 : -1;
    }
    for (const [i, panel] of panels.entries()) panel.hidden = i !== index;
    if (focus) tabs[index]?.focus();
  }
}

if (!customElements.get('bundle-tabs-component')) {
  customElements.define('bundle-tabs-component', BundleTabsComponent);
}
